import { mapState } from 'vuex';
import omit from 'lodash.omit';
import eventNames from '@/constants/analyticsEventNames';
import { datadogRum } from '@datadog/browser-rum';
import { telemetryUserAttributes as telemetryUserAttributesHelper, addAppSpecificData } from '@/utilities/analytics';
import { pendoReadyPromise } from '@/utilities/resolvers/pendoReadyResolver';

const PENDO_OMITTED_FIELDS = ['build_date', 'srp_refresh', 'user_id', 'vue_spa', 'web_version'];

const instrumentationMixin = {
  computed: {
    ...mapState([
      'devTools',
    ]),
    telemetryUserAttributes() {
      return telemetryUserAttributesHelper(this.$store.getters?.currentUser);
    },
  },
  data() {
    return {
      eventNames,
    };
  },
  methods: {
    log(message) {
      const loggingEnabled = this.devTools ? this.devTools.analyticsLoggingEnabled : false;
      if (import.meta.env.DEV && loggingEnabled) {
        window.console.info(`Analytics - EVENT: ${message}`);
      }
    },

    trackDatadog(title, data) {
      if (import.meta.env.VITE_THIRD_PARTY_JS_ENABLED === 'true') {
        const localData = addAppSpecificData(data);
        datadogRum.addAction(title, localData);
      }
    },
    trackMixPanel(title, data = {}) {
      const localData = addAppSpecificData(data);
      if (window.mixpanel) {
        window.mixpanel.track(title, localData);
      }
    },
    async trackPendo(title, data = {}) {
      const localData = addAppSpecificData(data);
      await pendoReadyPromise;
      if (window.pendo) {
        window.pendo.track(title, omit(localData, PENDO_OMITTED_FIELDS));
      }
    },
    track(title, data, { withDatadog = true, withMixpanel = true, withPendo = false } = {}) {
      const userId = data?.userId || data?.user_id || this?.telemetryUserAttributes?.user_id;
      const compiledData = {
        user_id: userId,
        ...data,
      };
      this.log(`${title}, DATA: ${JSON.stringify(compiledData)}`);
      if (withDatadog) {
        this.trackDatadog(title, compiledData);
      }

      const ffSlugs = this.$store?.state?.featureFlags?.featureFlagSlugs;
      const pendoFlagSlug = ffSlugs?.enableTrackPendoInsteadOfMixpanel;
      const pendoFlagEnabled = this.$store?.state?.featureFlags?.flags?.[pendoFlagSlug];
      if (withPendo && pendoFlagEnabled) {
        this.trackPendo(title, compiledData);
      }

      if (withMixpanel) {
        this.trackMixPanel(title, compiledData);
      }
    },
    recordAuditTrail(
      auctionId,
      bidAmount,
      sellerOrBuyer,
      acceptedOrRejected,
      fromFastActionPanel = false,
      fromAuctionDetailPage = false,
      source = '',
    ) {
      const { currentUser } = this.$store.getters;
      const auditMessage = `Auction #${auctionId} the ${sellerOrBuyer}, user ${currentUser.user_id}, ${acceptedOrRejected} $${bidAmount} at ${new Date()}, from web`;
      this.track('Audit Trail', {
        auction_id: auctionId,
        value: bidAmount,
        label: auditMessage,
        from_fast_action_panel: fromFastActionPanel,
        source,
      });

      if (acceptedOrRejected === 'accepted') {
        this.track('Payment Pending', {
          auction_id: auctionId,
          amount: bidAmount,
          user_id: currentUser.user_id,
          who_accepted: sellerOrBuyer,
          from_fast_action_panel: fromFastActionPanel,
          from_auction_detail_page: fromAuctionDetailPage,
          source,
        });
      }
    },
    setupCustomAnalyticsAttributes() {
      const { currentUser } = this.$store.getters;
      if (window.mixpanel) {
        window.mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN);
        window.mixpanel.people.set(this.telemetryUserAttributes);
        window.mixpanel.identify(currentUser.user_id);
      }

      if (import.meta.env.VITE_THIRD_PARTY_JS_ENABLED === 'true') {
        datadogRum.setUser({ id: currentUser.id, ...this.telemetryUserAttributes });
      }

      this.log(`Tracking User Set: ${JSON.stringify(this.telemetryUserAttributes)}`);
    },

    noticeError(error, customAttributes = {}) {
      const errorMessage = error.message || 'NO MESSAGE';
      this.log(`Error Noticed - MESSAGE: ${errorMessage}, DATA: ${JSON.stringify(customAttributes)}`);
      if (import.meta.env.VITE_THIRD_PARTY_JS_ENABLED === 'true') {
        datadogRum.addError(error, customAttributes);
      }
    },

    resetMixpanel() {
      if (window.mixpanel) {
        window.mixpanel.reset();
        this.log(`No longer tracking for User: ${JSON.stringify(this.telemetryUserAttributes)}`);
      }
    },
  },
};

export default instrumentationMixin;
